import { useEffect, useRef, useState, useCallback } from 'react'
import type { Banner } from '@/types'

interface BannerDisplayProps {
  banners: Banner[]
  position: 'header' | 'between_articles'
  className?: string
}

/** Embaralha um array (Fisher-Yates) retornando uma cópia. */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default function BannerDisplay({ banners, position, className = '' }: BannerDisplayProps) {
  const activeBanners = banners.filter(b => b.position === position && b.isActive)

  // Fila embaralhada — recalculada só quando a lista muda
  const [queue, setQueue] = useState<Banner[]>(() => shuffle(activeBanners))
  const [queueIndex, setQueueIndex] = useState(0)
  const [visible, setVisible] = useState(true)           // controla o fade
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Recria a fila quando os banners ativos mudarem
  useEffect(() => {
    setQueue(shuffle(activeBanners))
    setQueueIndex(0)
    setVisible(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBanners.length])

  const advance = useCallback(() => {
    if (queue.length <= 1) return
    // 1) fade-out
    setVisible(false)
    setTimeout(() => {
      setQueueIndex(prev => {
        const next = prev + 1
        // quando chega ao fim, reembaralha e reinicia
        if (next >= queue.length) {
          setQueue(shuffle(queue))
          return 0
        }
        return next
      })
      // 2) fade-in
      setVisible(true)
    }, 400) // duração do fade-out em ms
  }, [queue])

  // Troca automática a cada 40 s
  useEffect(() => {
    if (queue.length <= 1) return
    timerRef.current = setInterval(advance, 40000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [advance, queue.length])

  if (queue.length === 0) return null

  const banner = queue[queueIndex] ?? queue[0]

  const renderMedia = () => {
    const mediaUrl = banner.mediaUrl
    const isVideo =
      banner.mediaType === 'video' ||
      mediaUrl?.endsWith('.mp4') ||
      mediaUrl?.endsWith('.webm')

    if (isVideo) {
      return (
        <video
          src={mediaUrl}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-contain"
          aria-label={banner.title}
          style={{ pointerEvents: 'none' }}
        />
      )
    }

    return (
      <img
        src={mediaUrl}
        alt={banner.title}
        className="w-full h-full object-contain"
        loading="lazy"
      />
    )
  }

  const handleDotClick = (idx: number) => {
    if (timerRef.current) clearInterval(timerRef.current)
    setVisible(false)
    setTimeout(() => {
      setQueueIndex(idx)
      setVisible(true)
      // reinicia o timer
      timerRef.current = setInterval(advance, 40000)
    }, 400)
  }

  // Wrapper responsivo: largura total em mobile, máximo 630px centralizado em desktop
  const content = (
    <div
      className={`relative overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-lg w-full ${className}`}
      style={{ maxWidth: '630px', aspectRatio: '630 / 90' }}
    >
      {/* Camada com fade */}
      <div
        style={{
          transition: 'opacity 0.4s ease-in-out',
          opacity: visible ? 1 : 0,
          width: '100%',
          height: '100%',
          position: 'absolute',
          inset: 0,
        }}
      >
        {renderMedia()}
      </div>

      {/* Indicadores de posição */}
      {queue.length > 1 && (
        <div className="absolute bottom-1 right-2 flex gap-1 z-10">
          {queue.map((_, i) => (
            <button
              key={i}
              onClick={() => handleDotClick(i)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === queueIndex ? 'bg-white scale-125' : 'bg-white/40'
              }`}
              aria-label={`Banner ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )

  if (banner.linkUrl) {
    return (
      <div className="flex justify-center py-3 px-3 sm:py-4 sm:px-4 w-full">
        <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" aria-label={banner.title} className="w-full flex justify-center">
          {content}
        </a>
      </div>
    )
  }

  return <div className="flex justify-center py-3 px-3 sm:py-4 sm:px-4 w-full">{content}</div>
}
